/**
 * 模組頁面代碼: 1310-UI
 * 模組說明: 大樓資料功能頁面
 * @module BuildingContainer
 */

// ----- import from local libraries -----
import {
    React,
    PropTypes,
    connect,
    BaseComponent,
    AccessModeEnums,
} from '../../components/BaseComponent';

// ----- import from local components -----
import Header from '../../features/main-frame/components/Header';
import Footer from '../../features/main-frame/components/Footer';
//import Sidebar from '../../features/main-frame/components/Sidebar';
import BuildingPage from './components/BuildingPage';
import BuildingModal from './components/BuildingModal';
import ControlDeviceListModal from './components/ControlDeviceListModal';
import ControlDeviceModal from './components/ControlDeviceModal';
import { AlertModal, AlertModalModeEnums } from '../../components/AlertModal';
import { MessageModal, MessageModalModeEnums } from '../../components/MessageModal';

// ----- import from local actions -----
import {
    addBuilding,
    editBuilding,
    editBuildingStatus,
    fetchBuilding,
    fetchBuildingList, 
    fetchBuildingSelection,
} from '../../redux/actions/BuildingActions';
import {
    addControlDevice,
    editControlDevice,
    editControlDeviceStatus,
    deleteControlDevice,
    fetchControlDevice,
    fetchControlDeviceList,
    syncControlDeviceMac,
} from '../../redux/actions/ControlDeviceActions';

// ----- constants and enums -----
const BuildingModalModeEnums = {
    NEW: 'new',
    EDIT: 'edit',
};

const ControlDeviceModalModeEnums = {
    NEW: 'new',
    EDIT: 'edit',
};

const ACTION_TARGET_BUILDING = 'building';
const ACTION_TARGET_CONTROL_DEVICE = 'controlDevice';

const RESPONSE_OK = 200;
const TITLE_CTRLDEV_STATUS = '修改梯控裝置狀態';
const TITLE_CTRLDEV_DELETE = '刪除梯控裝置';
const TITLE_BUILDING_STATUS = '修改大樓裝置狀態';

class BuildingContainer extends BaseComponent {
    constructor(props) {
        super(props);
        // 创建 ref
        this.buildingModalRef = React.createRef();
        this.controlDeviceListModalRef = React.createRef();
        this.controlDeviceModalRef = React.createRef();
        this.alertModalRef = React.createRef();
        this.messageModalRef = React.createRef();
        this.buildingModal = null;
        this.controlDeviceListModal = null;
        this.controlDeviceModal = null;
        this.alertModal = null;
        this.messageModal = null;

        this.state = {
            needBuildingLoad: false,
            needControlDeviceListLoad: false,
            controlDeviceModalMode: ControlDeviceModalModeEnums.NEW,
            buildingModalMode: BuildingModalModeEnums.NEW,
            actionMode: AlertModalModeEnums.ENABLE,
            actionTarget: '',
            building: null,
            controlDevice: null,
            buildingId: null,
            alertMessage: '',
            actionIds: [],
            message: { mode: MessageModalModeEnums.ERROR, title: '', body: ''},
            search: { buildingId: 0 },
        };
        this.toggleBuildingModal = this.toggleBuildingModal.bind(this);
        this.toggleControlDeviceListModal = this.toggleControlDeviceListModal.bind(this);
        this.toggleControlDeviceModal = this.toggleControlDeviceModal.bind(this);
        this.toggleAlertModal = this.toggleAlertModal.bind(this);
        this.toggleMessageModal = this.toggleMessageModal.bind(this);
        this.onConfirm = this.onConfirm.bind(this);
    }

    toggleBuildingModal(isShow, mode = BuildingModalModeEnums.NEW, index = null) {
        if (this.buildingModal) {
            if (mode === BuildingModalModeEnums.EDIT) {
                this.setState({ building: this.props.buildingList[index] });
            } else {
                this.setState({ building: null });
            }

            if (isShow) {
                this.buildingModal.show();
            } else {
                this.buildingModal.hide();
            }
            this.setState({ buildingModalMode: mode });
        }
    }

    toggleControlDeviceListModal(isShow, index = null) {
        if (this.controlDeviceListModal) {
            if (index !== null) {
                const building = this.props.buildingList[index];
                this.setState({ building: building});
                this.props.fetchControlDeviceList(building.buildingId).then(() => {
                    this.setState({ needControlDeviceListLoad: true });
                });
            }
            if (isShow) {
                this.controlDeviceListModal.show();
            } else {
                this.controlDeviceListModal.hide();
            }
        }
    }

    toggleControlDeviceModal(isShow, mode = ControlDeviceModalModeEnums.NEW, index = null) {
        if (this.controlDeviceModal) {
            if (mode === ControlDeviceModalModeEnums.EDIT) {
                this.setState({ controlDevice: this.props.controlDeviceList[index] });
            } else {
                this.setState({ controlDevice: null });
            }

            if (isShow) {
                this.controlDeviceModal.show();
            } else {
                this.controlDeviceModal.hide();
            }
            this.setState({ controlDeviceModalMode: mode });
        }
    }

    toggleAlertModal(isShow, mode = AlertModalModeEnums.ENABLE, target, message = '', buildingIds = []) {
        if (this.alertModal) {
            if (isShow) {
                let alertMessage = message;
                if (alertMessage === '') {
                    let length = buildingIds.length;
                    if (mode === AlertModalModeEnums.ENABLE) {
                        alertMessage = `啟用 [ ${length} ] 筆資料`;
                    } else if (mode === AlertModalModeEnums.DISABLE) {
                        alertMessage = `停用 [ ${length} ] 筆資料`;
                    }
                }
                this.setState({ actionMode: mode });
                this.setState({ alertMessage: alertMessage });
                this.setState({ actionTarget: target});
                this.setState({ actionIds: buildingIds });
                this.alertModal.show();
            } else {
                this.alertModal.hide();
            }
        }
    }

    toggleMessageModal(isShow, mode = MessageModalModeEnums.ERROR, title = '', message = '') {
        if (this.messageModal) {
            if (isShow) {
                let body = message;
                const statusCode = message?.code || '';
                if (statusCode !== '') {
                    body = `[ ${statusCode} ] ${message.message}`;
                }
                this.setState({ message: { mode: mode, title: title, body: body }});
                this.messageModal.show();
            } else {
                this.messageModal.hide();
            }
        }
    }

    onConfirm() {
        const actionTarget = this.state.actionTarget;
        const actionMode = this.state.actionMode;
        const actionIds = this.state.actionIds;

        if (actionTarget === ACTION_TARGET_CONTROL_DEVICE) {
            const deviceId = actionIds[0];
            if (actionMode === AlertModalModeEnums.ENABLE || actionMode === AlertModalModeEnums.DISABLE) {
                const status = actionMode === AlertModalModeEnums.ENABLE ? 'A' : 'I';
                this.props.editControlDeviceStatus(deviceId, status).then((response) => {
                    const statusCode = response.payload.code;
                    if (statusCode !== RESPONSE_OK) {
                        this.toggleAlertModal(false);
                        this.toggleMessageModal(true, MessageModalModeEnums.ERROR, TITLE_CTRLDEV_STATUS, response.payload);
                    } else {
                        const buildingId = this.state.building?.buildingId;
                        this.props.fetchControlDeviceList(buildingId).then(() => {
                            this.setState({ needControlDeviceListLoad: true });
                            this.toggleAlertModal(false);
                            this.toggleControlDeviceListModal(true);
                        });
                    }
                });
            } else if (actionMode === AlertModalModeEnums.DELETE) {
                this.props.deleteControlDevice(deviceId).then((response) => {
                    const statusCode = response.payload.code;
                    if (statusCode !== RESPONSE_OK) {
                        this.toggleAlertModal(false);
                        this.toggleMessageModal(true, MessageModalModeEnums.ERROR, TITLE_CTRLDEV_DELETE, response.payload);
                    } else {
                        const buildingId = this.state.building?.buildingId;
                        this.props.fetchControlDeviceList(buildingId).then(() => {
                            this.setState({ needControlDeviceListLoad: true });
                            this.toggleAlertModal(false);
                            this.toggleControlDeviceListModal(true);
                        });
                    }
                });
            }
        } else if (actionTarget === ACTION_TARGET_BUILDING) {
            if (actionMode === AlertModalModeEnums.ENABLE || actionMode === AlertModalModeEnums.DISABLE) {
                let status = actionMode === AlertModalModeEnums.ENABLE ? 'A' : 'I';
                this.props.editBuildingStatus(actionIds, status).then((response) => {
                    const statusCode = response.payload.code;
                    if (statusCode !== RESPONSE_OK) {
                        this.toggleAlertModal(false);
                        this.toggleMessageModal(true, MessageModalModeEnums.ERROR, TITLE_BUILDING_STATUS, response.payload);
                    } else {
                        this.props.fetchBuildingList(0).then(() => {
                            this.setState({ needBuildingLoad: true });
                            this.toggleAlertModal(false);
                        });
                    }
                });
            }
        }
    }

    componentDidMount() {
        // 頁面所需資料初始化
        this.props.fetchBuildingSelection();
        this.props.fetchBuildingList(0).then(() => {
            this.setState({ needBuildingLoad: true });
        });
        this.buildingModal = new bootstrap.Modal(this.buildingModalRef.current);
        this.controlDeviceListModal = new bootstrap.Modal(this.controlDeviceListModalRef.current);
        this.controlDeviceModal = new bootstrap.Modal(this.controlDeviceModalRef.current);
        this.alertModal = new bootstrap.Modal(this.alertModalRef.current);
        this.messageModal = new bootstrap.Modal(this.messageModalRef.current);
    }
    
    componentDidUpdate(prevProps, prevState) {
        const socketMessage = this.props.newSocketMessage;
        if (socketMessage && prevProps.newSocketMessage !== socketMessage) {
            // 處理新的 socket 消息
            if (socketMessage && socketMessage.event === 'sync_mac') {
                const payload = socketMessage.payload;
                const controlDevice = this.state.controlDevice;
                if (controlDevice && controlDevice.deviceHost === payload.deviceHost) {
                    const updatedControlDevice = {
                        ...controlDevice,
                        deviceMac: payload.deviceMac
                    };
                    this.setState({ controlDevice: updatedControlDevice });
                }
            }
        }
    }

    componentWillUnmount() {
        this.toggleBuildingModal(false);
        this.toggleControlDeviceListModal(false);
        this.toggleControlDeviceModal(false);
        this.toggleAlertModal(false);
        this.toggleMessageModal(false);
    }

    render() {
        return (
            <React.Fragment>
                <Header />
                <div className="header__Placeholder"></div>
                <div className="m__wrap">
                    <div className="m__contents">
                        {/* <Sidebar /> */}
                        <BuildingPage
                            {...this.props}
                            needBuildingLoad={this.state.needBuildingLoad}
                            setNeedBuildingLoad={(bLoad) => this.setState({ needBuildingLoad: bLoad })}
                            onNewBuilding={() => this.toggleBuildingModal(true, BuildingModalModeEnums.NEW)}
                            onEditBuilding={(index) => this.toggleBuildingModal(true, BuildingModalModeEnums.EDIT, index)}
                            onEditBuildingStatus={(mode, message, buildingId) => this.toggleAlertModal(true, mode, ACTION_TARGET_BUILDING, message, [buildingId])}
                            onBatchHandle={(mode, buildingIds) => this.toggleAlertModal(true, mode, ACTION_TARGET_BUILDING, '', buildingIds)}
                            setSearchBuilding={(buildingId) => this.setState({ search: { buildingId: buildingId } })}
                            onControlDeviceList={(index) => {
                                this.toggleControlDeviceListModal(true, index);
                            }}
                        />
                    </div>
                </div>
                <Footer />
                <ControlDeviceListModal 
                    {...this.props}
                    ref={this.controlDeviceListModalRef}
                    checkPermission={this.checkPermission.bind(this)}
                    building={this.state.building}
                    needControlDeviceListLoad={this.state.needControlDeviceListLoad}
                    setNeedControlDeviceListLoad = {(bLoad) => this.setState({ needControlDeviceListLoad: bLoad })}                    
                    onNewControlDevice={() => {
                        this.toggleControlDeviceModal(true, ControlDeviceModalModeEnums.NEW);
                        this.toggleControlDeviceListModal(false);
                    }}
                    onEditControlDevice={(index) => {
                        this.toggleControlDeviceModal(true, ControlDeviceModalModeEnums.EDIT, index);
                        this.toggleControlDeviceListModal(false);
                    }}
                    onDeleteControlDevice={(message, deviceId) => {
                        this.toggleAlertModal(true, AccessModeEnums.DELETE, ACTION_TARGET_CONTROL_DEVICE, message, [deviceId]);
                        this.toggleControlDeviceListModal(false);
                    }}
                    onEditControlDeviceStatus={(mode, message, deviceId) => {
                        this.toggleAlertModal(true, mode, ACTION_TARGET_CONTROL_DEVICE, message, [deviceId]);
                        this.toggleControlDeviceListModal(false);
                    }}
                />
                <BuildingModal 
                    {...this.props}
                    ref={this.buildingModalRef}
                    mode={this.state.buildingModalMode}
                    building={this.state.building}
                    searchBuilding={this.state.search}
                    setNeedBuildingLoad={(bLoad) => this.setState({ needBuildingLoad: bLoad })}
                    onErrorHandle={(title, message) => this.toggleMessageModal(true, MessageModalModeEnums.ERROR, title, message)}
                />
                <ControlDeviceModal
                    {...this.props}
                    ref={this.controlDeviceModalRef}
                    mode={this.state.controlDeviceModalMode}
                    buildingId={this.state.building?.buildingId}
                    controlDevice={this.state.controlDevice}
                    setNeedControlDeviceListLoad = {(bLoad) => {
                        this.setState({ needControlDeviceListLoad: bLoad });
                        this.toggleControlDeviceListModal(true);
                    }}
                    onErrorHandle={(title, message) => this.toggleMessageModal(true, MessageModalModeEnums.ERROR, title, message)}
                />
                <AlertModal
                    ref={this.alertModalRef}
                    mode={this.state.actionMode}
                    message={this.state.alertMessage}
                    onConfirm={() => this.onConfirm()}
                    onCancel={() => this.setState({ needBuildingLoad: true })}
                />
                <MessageModal
                    ref={this.messageModalRef}
                    mode={this.state.message.mode}
                    title={this.state.message.title}
                    message={this.state.message.body}
                />
            </React.Fragment>
        );
    }
}

BuildingContainer.propTypes = {
    userPermissions: PropTypes.array,
    buildingSelection: PropTypes.array,
    buildingList: PropTypes.array,
    controlDeviceList: PropTypes.array,
    newSocketMessage: PropTypes.any,
};

BuildingContainer.defaultProps = {
    userPermissions: [],
    buildingSelection: [],
    buildingList: [],
    controlDeviceList: [],
    newSocketMessage: null,
};

function mapStateToProps(state) {
    return {
        userPermissions: state.access.userPermissions,
        buildingSelection: state.building.buildingSelection.data,
        buildingList: state.building.buildingList.data,
        controlDeviceList: state.controlDevice.controlDeviceList.data,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        addBuilding: (building) => {
            return dispatch(addBuilding(building));
        },
        editBuilding: (building) => {
            return dispatch(editBuilding(building));
        },
        editBuildingStatus: (buildingIds, status) => {
            return dispatch(editBuildingStatus({ buildingIds, status} ));
        },
        fetchBuilding: (buildingId) => {
            return dispatch(fetchBuilding(buildingId));
        },
        fetchBuildingSelection: () => {
            return dispatch(fetchBuildingSelection());
        },
        fetchBuildingList: (buildingId) => {
            return dispatch(fetchBuildingList({ buildingId }));
        },
        addControlDevice: (controlDevice) => {
            return dispatch(addControlDevice(controlDevice));
        },
        editControlDevice: (controlDevice) => {
            return dispatch(editControlDevice(controlDevice));
        },
        editControlDeviceStatus: (deviceId, status) => {
            return dispatch(editControlDeviceStatus({ deviceId, status }));
        },
        deleteControlDevice: (deviceId) => {
            return dispatch(deleteControlDevice(deviceId));
        },
        fetchControlDeviceList: (buildingId) => {
            return dispatch(fetchControlDeviceList({ buildingId }));
        },
        syncControlDeviceMac: (deviceHost) => {
            return dispatch(syncControlDeviceMac({ deviceHost }));
        },
    };    
}

export default connect(mapStateToProps, mapDispatchToProps)(BuildingContainer);